<template>
  <profile-edit-section-card
    v-if="localState"
    title="Rate"
    breadcrumb-label="Profile"
    :saving="isSaving"
    :has-unsaved-changes="hasUnsavedChanges"
    @breadcrumb-click="cancelEdit"
    @save="handleSave"
    @cancel="cancelEdit"
  >
    <fieldset class="mb-24">
      <legend class="form__label pb-8">Set your base hourly rate for 1 child</legend>
      <p class="text-caption mb-16">You can update your rate at anytime</p>
      <div class="inline-flex items-center gap-16">
        <poppins-text-field
          id="min-rate"
          v-model="localState.paymentRateMin"
          label-name="$ Min"
          type="text"
          pattern="[0-9]*"
          maxlength="3"
          inputmode="numeric"
          :limit-input-to-pattern="true"
        />
        <span>to</span>
        <poppins-text-field
          id="max-rate"
          v-model="localState.paymentRateMax"
          label-name="$ Max"
          type="text"
          pattern="[0-9]*"
          maxlength="3"
          inputmode="numeric"
          :limit-input-to-pattern="true"
        />
      </div>
    </fieldset>
    <poppins-text-field
      id="increase-per-child-rate"
      v-model="localState.increasePerChildRate"
      label-name="$ Rate increase per additional child"
      type="text"
      pattern="[0-9]*"
      maxlength="3"
      inputmode="numeric"
      :limit-input-to-pattern="true"
      class="mb-24"
    />
    <poppins-dropdown
      id="max-number-of-children"
      v-model="localState.maxNumberOfChildren"
      class="mb-24"
      label-name="How many children are you willing to care for at a time?"
      :options="maxChildrenOptions"
    />
    <poppins-dropdown
      id="distance-willing-to-travel"
      v-model="localState.distanceWillingToTravel"
      label-name="How many miles from home are you willing to work?"
      :options="distanceOptions"
    />
  </profile-edit-section-card>
</template>

<script lang="ts">
import PoppinsTextField from '@/components/textfield/TextField.vue';
import PoppinsDropdown from '@/components/dropdown/Dropdown.vue';
import ProfileEditSectionCard from '../../components/ProfileEditSectionCard.vue';
import profileEditMixin from '../../mixins/profileEditMixin.js';
import { CHILD_CARE_MAX_CHILDREN_OPTIONS, distanceOptions } from '../../__types__/CareTypeFormOptions.js';
import type { BabysitterProfileFormState } from '../../__types__/BabysitterProfileTypes.js';

export default {
  name: 'ChildcareRateForm',
  components: {
    ProfileEditSectionCard,
    PoppinsTextField,
    PoppinsDropdown,
  },
  mixins: [profileEditMixin],
  data(): any {
    return {
      localState: null,
      originalState: null,
    };
  },
  computed: {
    distanceOptions: () => distanceOptions(),
    maxChildrenOptions: () => CHILD_CARE_MAX_CHILDREN_OPTIONS,
    hasUnsavedChanges(): boolean {
      if (!this.localState || !this.originalState) return false;
      return JSON.stringify(this.localState) !== JSON.stringify(this.originalState);
    },
  },
  watch: {
    $route() {
      this.initializeFormState();
    },
    formState: {
      handler(newState) {
        if (newState) {
          this.initializeFormState();
        }
      },
      immediate: true,
    },
  },
  mounted() {
    this.initializeFormState();
  },
  methods: {
    centsToDollars(cents: number | null | undefined): string {
      if (cents == null || cents === 0) return '';
      return String(Math.round(cents / 100));
    },
    initializeFormState() {
      const state = this.formState as BabysitterProfileFormState | null;
      if (!state) return;

      this.localState = {
        paymentRateMin: String(state.paymentRate?.min ? Math.round(state.paymentRate.min / 100) : 15),
        paymentRateMax: String(state.paymentRate?.max ? Math.round(state.paymentRate.max / 100) : 25),
        increasePerChildRate: this.centsToDollars(state.increasePerChildRate),
        maxNumberOfChildren: String(state.maxNumberOfChildren || 1),
        distanceWillingToTravel: String(state.distanceWillingToTravel || 10),
      };
      this.originalState = JSON.parse(JSON.stringify(this.localState));
    },
    async handleSave() {
      if (!this.localState) return;

      if (this.hasUnsavedChanges) {
        await this.saveSection({
          paymentRate: {
            min: Number(this.localState.paymentRateMin) * 100,
            max: Number(this.localState.paymentRateMax) * 100,
          },
          increasePerChildRate: this.localState.increasePerChildRate ? Number(this.localState.increasePerChildRate) * 100 : null,
          maxNumberOfChildren: Number(this.localState.maxNumberOfChildren),
          distanceWillingToTravel: Number(this.localState.distanceWillingToTravel),
        });
      }
    },
  },
};
</script>
